import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { LS } from '../utils/storage';
import { apiFetch, apiJson } from '../utils/api';
import { isPreviewableMime } from '../utils/media';
import { setAdminId } from '../utils/admin';
import { notifyIncomingMessage } from '../utils/notify';
import { syncPushSubscription, isPushOn } from '../utils/push';
import { detectDevice } from '../utils/presence';
import { isEditableMessage } from '../utils/messageActions';
import { setAvatarMap, setAvatar } from '../utils/avatars';
import { isMobile, isTouchDevice, CHAT_LINK_PREFIX, CHAT_LINK_SUFFIX, channelsRef, onlineUsersRef, gestureState } from '../constants';
import { useMediaQuery, COMPACT_QUERY, NARROW_QUERY, isNarrowViewport, isCompactViewport } from '../hooks/useMediaQuery';
import { useFileUpload } from '../hooks/useFileUpload';
import { useChatSocket } from '../hooks/useChatSocket';
import { useChannelMessages } from '../hooks/useChannelMessages';
import { toast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { AccountSettingsModal } from '../components/modals/AccountSettingsModal';
import { GuestNicknameModal } from '../components/modals/GuestNicknameModal';
import { SiteNav } from '../components/layout/SiteNav';
import { ChatNavActions, NotificationSettings } from '../components/layout/ChatNavActions';
import { Icon } from '../components/Icon';
import { UserList, ReplyingBanner } from '../components/layout/UserList';
import { NotificationPanel } from '../components/layout/NotificationPanel';
import { ChannelSidebar, ChannelIcon } from '../components/layout/ChannelSidebar';
import { useBackClose } from '../utils/backStack';
import { ChatWindow } from '../components/chat/ChatWindow';
import { ChannelPinBar } from '../components/chat/ChannelPinBar';
import { InputForm, UploadProgress, ImagePreviewModal, TypingIndicator } from '../components/chat/InputForm';
import { Lightbox } from '../components/chat/Lightbox';
import { uiZoom } from '../utils/uiScale';

const CHAT_LINK_RE = /https?:\/\/\S*[?&]ch=(\d+)[&?]msg=(\d+)\S*/g;
const SEND_TIMEOUT_MS = 15000;
const READ_SYNC_DELAY_MS = 1200;

// 사이드바와 같은 순서(미분류 → 카테고리 순)로 정렬된 채널 목록
const sortChannelsLikeSidebar = (channels, categoryOrder) => {
    const grouped = {};
    channels.forEach(ch => { (grouped[ch.category || ''] ||= []).push(ch); });
    const cats = categoryOrder.filter(c => grouped[c]);
    Object.keys(grouped).forEach(c => { if (c && !cats.includes(c)) cats.push(c); });
    return ['', ...cats].filter(c => grouped[c]).flatMap(c => grouped[c]);
};

const useStoredToggle = (key, fallback) => {
    const [value, setValue] = useState(() => LS.getBool(key, fallback));
    const ref = useRef(value);
    useEffect(() => { ref.current = value; }, [value]);
    return [value, setValue, ref];
};

const isDmChannel = (ch) => !!ch?.name?.startsWith('_private_');
const defaultNotifyOf = (ch) => (isDmChannel(ch) ? 'all' : 'mentions');
const newClientId = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export default function ChatPage() {
    const { user, signOut, replaceToken, updateUser } = useAuth();
    const [clientData, setClientData] = useState(() => ({ ...user }));
    const isAdmin = !!clientData.isAdmin;
    const isMember = !!clientData.loginId;

    // ── 채널 / 메시지 ───────────────────────────────────────
    const [channels, setChannels] = useState([]);
    const [categoryOrder, setCategoryOrder] = useState([]);
    const [currentChannelId, setCurrentChannelId] = useState(null);
    const currentChannelIdRef = useRef(null);
    const [unread, setUnread] = useState({});
    const [target, setTarget] = useState(null); // { chId, msgId } 메시지 링크·검색·알림으로 이동할 대상
    const messages = useChannelMessages();
    const { ensureLoaded, jumpTo, loadOlder, loadNewer, loadLatest, resync } = messages; // 참조가 고정된 함수들
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    // ── 읽음 위치 · 채널 알림 수준 (회원은 서버와 동기화) ───
    const [readState, setReadState] = useState({}); // chId → lastReadId
    const readStateRef = useRef(readState);
    readStateRef.current = readState;
    const [notifyLevels, setNotifyLevels] = useState({}); // chId → 'all' | 'mentions' | 'none' (null = 기본값)
    const notifyLevelsRef = useRef(notifyLevels);
    notifyLevelsRef.current = notifyLevels;
    const readSyncTimers = useRef({});
    const readSyncPending = useRef({}); // chId → 아직 서버에 못 보낸 읽음 위치

    // ── 전송 중 메시지 · 저장한 메시지 · 수정 요청 ──────────
    const [pending, setPending] = useState({}); // chId → [{ clientId, message, replyToId, status, error }]
    const pendingTimers = useRef({});
    const [savedKeys, setSavedKeys] = useState(() => new Set());
    const [editRequest, setEditRequest] = useState(null);
    const joinedOnce = useRef(false);

    // ── 공지 ────────────────────────────────────────────────
    const [channelPins, setChannelPins] = useState({});
    const channelPinsRef = useRef(channelPins);
    channelPinsRef.current = channelPins;
    const [pinCollapsed, setPinCollapsed] = useState(isMobile);
    const autoCollapsePin = useRef(true);
    // 방마다 내 공지 표시 방식: chId → 'collapse'(항상 접힘) | 'expand'(항상 펼침). 없으면 전체 설정(기본)을 따른다.
    // 나에게만 적용된다. 회원은 계정 설정(preferences 의 pin_<채널ID>)에 저장해 기기 간에 같고, 게스트는 이 브라우저에만.
    const [pinModes, setPinModes] = useState(() => {
        try { return JSON.parse(LS.get('bcPinModes') || '{}') || {}; } catch { return {}; }
    });
    const pinModesRef = useRef(pinModes);
    pinModesRef.current = pinModes;
    const pinCollapsedFor = useCallback((chId) => {
        const mode = pinModesRef.current[chId];
        if (mode === 'collapse') return true;
        if (mode === 'expand') return false;
        return isMobile ? autoCollapsePin.current : false;
    }, []);
    const [newPinChannels, setNewPinChannels] = useState(() => {
        try { return new Set(JSON.parse(LS.get('bcNewPins') || '[]')); } catch { return new Set(); }
    });

    // ── 사람 / 알림 ─────────────────────────────────────────
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [systemMessages, setSystemMessages] = useState([]);
    const [typingUsers, setTypingUsers] = useState({});
    const [unreadNotifCount, setUnreadNotifCount] = useState(0);
    const [notifications, setNotifications] = useState(null); // null = 패널 닫힘
    const [notifyEnabled, setNotifyEnabled, notifyRef] = useStoredToggle('notify_general', false);
    const [mentionNotifyEnabled, setMentionNotifyEnabled, mentionNotifyRef] = useStoredToggle('notify_mention', true);
    const [messageSoundEnabled, setMessageSoundEnabled, messageSoundRef] = useStoredToggle('sound_message', false);
    const [mentionSoundEnabled, setMentionSoundEnabled, mentionSoundRef] = useStoredToggle('sound_mention', false);
    // 기본 켜짐. 단 사이트 알림 권한이 허용된 기기에서만 실제로 켜진다 (utils/push.js)
    const [pushOn, setPushOn] = useState(isPushOn);

    // ── 화면 상태 ───────────────────────────────────────────
    const [replyingTo, setReplyingTo] = useState(null);
    const [showAccountSettings, setShowAccountSettings] = useState(false);
    const [guestNickModal, setGuestNickModal] = useState(null); // null | { error }
    const [isChannelSidebarOpen, setChannelSidebarOpen] = useState(false);
    // 화면 크기 구간: 휴대폰(세로·가로)은 채널·접속자 목록 모두 서랍, 태블릿 세로는 접속자 목록만 겹쳐 열기
    const isCompact = useMediaQuery(COMPACT_QUERY);
    const isNarrow = useMediaQuery(NARROW_QUERY);
    // 휴대폰: 일반 메신저처럼 "채팅 목록 화면 → 방 들어가기 → ← 뒤로" (예전엔 ☰ 서랍으로 채널을 골랐다)
    // 알림·링크(?ch=)로 들어오면 바로 그 방을 연다.
    const [phoneList, setPhoneList] = useState(() => isCompactViewport() && !new URLSearchParams(window.location.search).get('ch'));
    const phoneListRef = useRef(phoneList);
    phoneListRef.current = phoneList;
    const showPhoneList = isCompact && phoneList;
    // 좁은 화면은 접속자 목록을 닫은 상태로 시작 (넓은 화면에서 열어둔 설정을 따르지 않음)
    const [isUserListCollapsed, setUserListCollapsed] = useState(() => (isNarrowViewport() ? true : LS.get('userListCollapsed') === 'true'));
    const [channelSidebarWidth, setChannelSidebarWidth] = useState(() => LS.getInt('channelSidebarWidth', 240));
    const [memberPanelWidth, setMemberPanelWidth] = useState(() => LS.getInt('memberPanelWidth', 260));
    const [isDragging, setIsDragging] = useState(false);
    const [isLayoutResizing, setLayoutResizing] = useState(false);
    const [pendingFiles, setPendingFiles] = useState(null);
    const [lightbox, setLightbox] = useState(null); // { items, index }
    const inputRef = useRef(null);
    const activeEditRef = useRef(null);
    const { confirm: confirmDialog, modal: confirmModal } = useConfirm();

    useEffect(() => { channelsRef.current = channels; }, [channels]);
    useEffect(() => { onlineUsersRef.current = onlineUsers; }, [onlineUsers]);
    useEffect(() => { currentChannelIdRef.current = currentChannelId; }, [currentChannelId]);
    useEffect(() => { if (!isNarrow) LS.set('userListCollapsed', isUserListCollapsed); }, [isUserListCollapsed, isNarrow]);
    // 화면이 좁아지면(회전·창 크기 변경) 열려 있던 겹침 패널을 닫는다
    useEffect(() => { if (isNarrow) setUserListCollapsed(true); }, [isNarrow]);
    useEffect(() => { if (!isCompact) setChannelSidebarOpen(false); }, [isCompact]);
    // 서랍은 한 번에 하나만 연다
    const toggleMemberPanel = () => { setUserListCollapsed(v => !v); if (isCompact) setChannelSidebarOpen(false); };
    // 휴대폰에서 서랍처럼 덮어 여는 목록은 뒤로가기로 닫는다
    useBackClose(isChannelSidebarOpen, () => setChannelSidebarOpen(false));
    useBackClose(isNarrow && !isUserListCollapsed, () => setUserListCollapsed(true));
    // 채널 제목 줄 오른쪽 칸: ChatWindow 가 검색·파일·저장함·알림 버튼을 여기에 그린다
    const [headerActions, setHeaderActions] = useState(null);
    useEffect(() => { LS.set('channelSidebarWidth', channelSidebarWidth); }, [channelSidebarWidth]);
    useEffect(() => { LS.set('memberPanelWidth', memberPanelWidth); }, [memberPanelWidth]);

    const handleLogout = useCallback(() => signOut(), [signOut]);

    // ── 읽음 위치 ───────────────────────────────────────────
    const lastReadOf = useCallback((chId) => Math.max(LS.getInt(`lastRead_${chId}`, 0), readStateRef.current[chId] || 0), []);

    // 읽음 위치는 모았다가 1.2초 뒤 서버에 보낸다. 그 사이에 새로고침·창 닫기가 일어나면
    // 서버는 옛 위치를 들고 있게 되고, 다시 들어왔을 때 읽은 방에 숫자가 다시 뜬다 → 나갈 때 남은 것을 즉시 보낸다.
    const sendReadNow = useCallback((chId, { keepalive = false } = {}) => {
        const messageId = readSyncPending.current[chId];
        if (!messageId) return;
        delete readSyncPending.current[chId];
        clearTimeout(readSyncTimers.current[chId]);
        apiFetch(`/api/channels/${chId}/read`, { method: 'POST', json: { lastReadId: messageId }, keepalive }).catch(() => {});
    }, []);

    const markRead = useCallback((chId, messageId) => {
        if (!chId || !messageId || messageId <= lastReadOf(chId)) return;
        LS.set(`lastRead_${chId}`, messageId);
        setReadState(prev => ({ ...prev, [chId]: messageId }));
        if (!isMember) return;
        readSyncPending.current[chId] = Math.max(readSyncPending.current[chId] || 0, messageId);
        clearTimeout(readSyncTimers.current[chId]);
        readSyncTimers.current[chId] = setTimeout(() => sendReadNow(chId), READ_SYNC_DELAY_MS);
    }, [isMember, lastReadOf, sendReadNow]);

    useEffect(() => {
        const flush = () => Object.keys(readSyncPending.current).forEach(chId => sendReadNow(chId, { keepalive: true }));
        const onHidden = () => { if (document.visibilityState === 'hidden') flush(); };
        window.addEventListener('pagehide', flush);
        document.addEventListener('visibilitychange', onHidden);
        return () => { window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', onHidden); };
    }, [sendReadNow]);

    // ── 채널 선택 ───────────────────────────────────────────
    const loadPin = useCallback((chId) => {
        apiJson(`/api/channels/${chId}/pin`).then(pin => setChannelPins(p => ({ ...p, [chId]: pin }))).catch(() => {});
    }, []);

    const markPinSeen = useCallback((chId) => setNewPinChannels(prev => {
        if (!prev.has(chId)) return prev;
        const next = new Set(prev); next.delete(chId);
        LS.set('bcNewPins', JSON.stringify([...next]));
        return next;
    }), []);

    const selectChannel = useCallback((chId, { load = true } = {}) => {
        setPhoneList(false);
        setCurrentChannelId(chId);
        setReplyingTo(null);
        setPinCollapsed(pinCollapsedFor(chId));
        markPinSeen(chId);
        LS.set('lastChannelId', chId);
        setUnread(prev => ({ ...prev, [chId]: { count: 0, hasMention: false } }));
        apiFetch(`/api/notifications/read-channel/${chId}`, { method: 'POST' }).catch(() => {});
        if (load) ensureLoaded(chId);
        if (channelPinsRef.current[chId] === undefined) loadPin(chId);
    }, [ensureLoaded, loadPin, markPinSeen, pinCollapsedFor]);

    // 메시지 링크·검색 결과·알림 → 해당 메시지 주변을 불러와서 이동
    const navigateToMessage = useCallback((chId, msgId) => {
        const id = Number(chId);
        if (!channelsRef.current.some(c => c.id === id)) return toast.error('접근할 수 없는 채널입니다.');
        selectChannel(id, { load: false });
        const token = Date.now();
        // 대상 주변 메시지를 다 받은 뒤에 스크롤한다 (먼저 스크롤하면 목록이 교체되면서 맨 아래로 돌아감)
        setTarget({ chId: id, msgId: Number(msgId), ready: false, token });
        Promise.resolve(jumpTo(id, msgId)).finally(() => {
            setTarget(t => (t && t.token === token ? { ...t, ready: true } : t));
        });
    }, [selectChannel, jumpTo]);

    const clearTarget = useCallback(() => setTarget(null), []);

    // ── 전송 중 메시지 ──────────────────────────────────────
    const updatePending = useCallback((chId, fn) => setPending(prev => {
        const list = fn(prev[chId] || []);
        const next = { ...prev };
        if (list.length) next[chId] = list; else delete next[chId];
        return next;
    }), []);

    const failPending = useCallback((clientId, error) => {
        clearTimeout(pendingTimers.current[clientId]);
        delete pendingTimers.current[clientId];
        setPending(prev => Object.fromEntries(Object.entries(prev).map(([ch, list]) => [ch, list.map(p => (p.clientId === clientId ? { ...p, status: 'failed', error } : p))])));
    }, []);

    const resolvePending = useCallback((clientId) => {
        clearTimeout(pendingTimers.current[clientId]);
        delete pendingTimers.current[clientId];
        setPending(prev => {
            let changed = false;
            const next = {};
            for (const [ch, list] of Object.entries(prev)) {
                const kept = list.filter(p => p.clientId !== clientId);
                if (kept.length !== list.length) changed = true;
                if (kept.length) next[ch] = kept;
            }
            return changed ? next : prev;
        });
    }, []);

    // ── WebSocket 이벤트 ────────────────────────────────────
    const handleSocketEvent = (data) => {
        if (messages.applyEvent(data) && data.type !== 'chat' && data.type !== 'channel_deleted' && data.type !== 'nickname_changed') return;

        switch (data.type) {
            case 'initial_state': {
                const rejoin = joinedOnce.current;
                joinedOnce.current = true;
                setClientData(prev => ({ ...prev, ...data.clientData }));
                setAdminId(data.adminId);
                setOnlineUsers(data.onlineUsers);
                setSystemMessages(data.systemMessages);
                setCategoryOrder(data.categoryOrder || []);
                setUnreadNotifCount(data.unreadNotifications || 0);
                setChannels(data.channels);
                setAvatarMap(data.avatars);
                sendPresenceRef.current(true);

                // 회원: 서버의 읽음 위치·알림 수준·안 읽은 수 반영 (다른 기기에서 읽은 것 포함)
                const state = data.channelState || {};
                if (Object.keys(state).length) {
                    setReadState(prev => {
                        const next = { ...prev };
                        for (const [ch, s] of Object.entries(state)) if (s.lastReadId) next[ch] = Math.max(next[ch] || 0, s.lastReadId);
                        return next;
                    });
                    setNotifyLevels(Object.fromEntries(Object.entries(state).map(([ch, s]) => [ch, s.notify || null])));
                    setUnread(prev => {
                        const next = { ...prev };
                        for (const [ch, s] of Object.entries(state)) {
                            if (Number(ch) === currentChannelIdRef.current) continue;
                            if (!s.lastReadId) continue;
                            // 이 기기가 서버보다 더 읽었고 그게 마지막 메시지까지라면 다 읽은 것이다
                            // (서버에 보내기 전에 새로고침된 경우). 서버 값이 뒤처져 생기는 가짜 숫자를 없앤다.
                            const local = LS.getInt(`lastRead_${ch}`, 0);
                            const caughtUp = s.latestId && local >= s.latestId;
                            next[ch] = caughtUp ? { count: 0, hasMention: false } : { count: s.unread, hasMention: s.mentions > 0 };
                        }
                        return next;
                    });
                    // 기기 쪽이 앞서 있으면 서버에도 알려 다른 기기·다음 접속에서 맞게 한다
                    for (const [ch, s] of Object.entries(state)) {
                        const local = LS.getInt(`lastRead_${ch}`, 0);
                        if (s.lastReadId && local > s.lastReadId) {
                            apiFetch(`/api/channels/${ch}/read`, { method: 'POST', json: { lastReadId: local } }).catch(() => {});
                        }
                    }
                }
                if (isMember || data.clientData?.loginId) {
                    apiJson('/api/saved').then(d => setSavedKeys(new Set((d.items || []).map(i => `${i.channelId}:${i.messageId}`)))).catch(() => {});
                    syncPushSubscription().then(ok => { if (ok) setPushOn(true); });
                }

                if (rejoin) {
                    // 끊겨 있던 동안의 메시지 이어 받기 + 이미 전달된 "전송 중" 메시지 정리
                    const loaded = Object.keys(messagesRef.current.channels).map(Number);
                    loaded.forEach(chId => resync(chId).then(items => {
                        const mine = items.filter(m => m.userId === data.clientData?.userId);
                        if (!mine.length) return;
                        updatePending(chId, list => list.filter(p => !mine.some(m => m.message === p.message)));
                    }));
                    if (currentChannelIdRef.current && data.channels.some(c => c.id === currentChannelIdRef.current)) break;
                }
                if (!data.channels.length) { setCurrentChannelId(null); break; }
                const params = new URLSearchParams(window.location.search);
                const linkCh = Number(params.get('ch')), linkMsg = Number(params.get('msg'));
                // 휴대폰 목록 화면에서는 방을 고를 때까지 아무 채널도 열지 않는다 (열어 두면 그 방 새 메시지가 읽음 처리된다)
                if (phoneListRef.current && !linkCh) break;
                const saved = currentChannelIdRef.current || LS.getInt('lastChannelId', 0);
                const initial = data.channels.find(c => c.id === (linkCh || saved)) || data.channels[0];
                if (linkCh && linkMsg && initial.id === linkCh) {
                    window.history.replaceState({}, '', window.location.pathname);
                    channelsRef.current = data.channels;
                    navigateToMessage(linkCh, linkMsg);
                } else {
                    selectChannel(initial.id);
                }
                break;
            }
            case 'chat':
                if (data.clientId) resolvePending(data.clientId);
                if (data.userId === clientData.userId) break;
                {
                    const level = notifyLevelsRef.current[data.channelId] || defaultNotifyOf(channelsRef.current.find(c => c.id === data.channelId));
                    const targeted = notifyIncomingMessage(data, clientData, {
                        messageSound: level === 'none' ? false : messageSoundRef.current,
                        mentionSound: level === 'none' ? false : mentionSoundRef.current,
                        generalNotify: level === 'all' ? true : level === 'none' ? false : notifyRef.current,
                        mentionNotify: level === 'none' ? false : mentionNotifyRef.current,
                    });
                    const viewing = data.channelId === currentChannelIdRef.current && document.visibilityState === 'visible';
                    if (!viewing) {
                        setUnread(prev => {
                            const cur = prev[data.channelId] || { count: 0, hasMention: false };
                            return { ...prev, [data.channelId]: { count: cur.count + 1, hasMention: cur.hasMention || targeted } };
                        });
                    }
                }
                break;
            case 'send_failed': if (data.clientId) failPending(data.clientId, data.message); break;
            case 'read_state':
                // 같은 계정의 다른 기기에서 읽음
                setReadState(prev => ({ ...prev, [data.channelId]: Math.max(prev[data.channelId] || 0, data.lastReadId) }));
                if (data.channelId !== currentChannelIdRef.current) setUnread(prev => ({ ...prev, [data.channelId]: { count: 0, hasMention: false } }));
                break;
            case 'notify_level': setNotifyLevels(prev => ({ ...prev, [data.channelId]: data.level })); break;
            case 'avatar_changed': setAvatar(data.userId, data.avatar); break;
            case 'userlist':
                if (data.channelId === currentChannelIdRef.current) {
                    setOnlineUsers(data.users);
                    setSystemMessages(data.systemMessages);
                }
                break;
            case 'new_mention': setUnreadNotifCount(data.unreadCount); break;
            case 'channel_created': setChannels(prev => (prev.some(c => c.id === data.channel.id) ? prev : [...prev, data.channel])); break;
            case 'channel_updated':
            case 'channel_privacy_updated':
                setChannels(prev => (prev.some(c => c.id === data.channel.id)
                    ? prev.map(c => (c.id === data.channel.id ? { ...c, ...data.channel } : c))
                    : [...prev, data.channel]));
                break;
            case 'channel_deleted':
                setChannels(prev => prev.filter(c => c.id !== data.channelId));
                if (currentChannelIdRef.current === data.channelId) setCurrentChannelId(null);
                break;
            case 'channels_reordered': setChannels(data.channels); break;
            case 'categories_updated': setCategoryOrder((data.categories || []).map(c => c.name)); break;
            case 'channelPinUpdated':
                setChannelPins(prev => ({ ...prev, [data.channelId]: data.pin }));
                if (data.pin && data.channelId !== currentChannelIdRef.current) {
                    setNewPinChannels(prev => { const next = new Set(prev).add(data.channelId); LS.set('bcNewPins', JSON.stringify([...next])); return next; });
                }
                // 지금 보는 방에 새 공지가 올라오면 그 방 설정대로 (기본: PC 펼침)
                if (data.pin && data.channelId === currentChannelIdRef.current) setPinCollapsed(pinCollapsedFor(data.channelId));
                break;
            case 'nickname_changed':
                if (data.userId === clientData.userId) {
                    setClientData(prev => ({ ...prev, username: data.newUsername }));
                    updateUser({ username: data.newUsername });
                    setGuestNickModal(null);
                    toast.success(`닉네임이 "${data.newUsername}"(으)로 변경되었습니다.`);
                }
                break;
            case 'nickname_change_error': setGuestNickModal({ error: data.message }); break;
            case 'guest_token': replaceToken(data.token); break;
            case 'typing':
                setTypingUsers(prev => {
                    const ch = { ...(prev[data.channelId] || {}) };
                    clearTimeout(ch[data.username]);
                    ch[data.username] = setTimeout(() => setTypingUsers(p => {
                        const c = { ...(p[data.channelId] || {}) }; delete c[data.username];
                        return { ...p, [data.channelId]: c };
                    }), 3000);
                    return { ...prev, [data.channelId]: ch };
                });
                break;
            case 'reaction_notification':
                toast.info(`${data.fromUsername}님이 내 메시지에 ${data.emoji} 반응을 남겼습니다.`);
                break;
            case 'announcement_block':
                if (data.clientId) resolvePending(data.clientId);
                toast.error(data.message);
                break;
            case 'force_logout':
                toast.error(data.reason || '세션이 종료되었습니다.');
                setTimeout(handleLogout, 1500);
                break;
            case 'error': toast.error(data.message || '연결 오류'); break;
            default: break;
        }
    };

    const sendWsMessage = useChatSocket({
        enabled: true,
        onEvent: handleSocketEvent,
        onAuthFailure: handleLogout,
        onKicked: (reason) => { toast.error(reason || '다른 곳에서 접속하여 연결이 종료되었습니다.'); setTimeout(handleLogout, 1500); },
    });

    useEffect(() => {
        if (currentChannelId) sendWsMessage({ type: 'channel_view', channelId: currentChannelId });
    }, [currentChannelId, sendWsMessage]);

    // 열린 채널이 없으면 마지막(없으면 첫) 채널을 연다. 휴대폰 목록 화면에서는 고를 때까지 비워 둔다.
    // (목록 화면에서 화면이 넓어지면 이 효과가 다시 채널을 연다)
    useEffect(() => {
        if (currentChannelId !== null || !channels.length || (isCompact && phoneList)) return;
        const saved = LS.getInt('lastChannelId', 0);
        selectChannel((channels.find(c => c.id === saved) || channels[0]).id);
    }, [currentChannelId, channels, selectChannel, isCompact, phoneList]);

    useEffect(() => {
        if (!clientData.loginId) return;
        apiJson('/api/auth/preferences').then(data => {
            const prefs = data.preferences || {};
            autoCollapsePin.current = prefs.autocollapsePin !== undefined ? !!prefs.autocollapsePin : true;
            // 서버에 저장된 방별 공지 설정이 기준 (다른 기기에서 바꾼 것도 반영)
            const modes = {};
            for (const [key, value] of Object.entries(prefs)) {
                const m = /^pin_(\d+)$/.exec(key);
                if (m && (value === 'collapse' || value === 'expand')) modes[m[1]] = value;
            }
            pinModesRef.current = modes;
            setPinModes(modes);
            LS.set('bcPinModes', JSON.stringify(modes));
            if (currentChannelIdRef.current) setPinCollapsed(pinCollapsedFor(currentChannelIdRef.current));
        }).catch(() => {});
    }, [clientData.loginId, pinCollapsedFor]);

    const changePinMode = useCallback((chId, mode) => {
        const next = { ...pinModesRef.current };
        if (mode) next[chId] = mode; else delete next[chId];
        pinModesRef.current = next;
        setPinModes(next);
        LS.set('bcPinModes', JSON.stringify(next));
        if (chId === currentChannelIdRef.current) setPinCollapsed(pinCollapsedFor(chId));
        if (clientData.loginId) {
            apiJson('/api/auth/preferences', { method: 'POST', json: { [`pin_${chId}`]: mode || null } })
                .catch(() => toast.error('공지 설정을 서버에 저장하지 못했습니다. 이 기기에만 적용됩니다.'));
        }
    }, [clientData.loginId, pinCollapsedFor]);

    // 앱이 보이는지 서버에 알림 (보고 있는 동안엔 푸시를 보내지 않음) + 돌아오면 지금 채널 안 읽음 해제
    useEffect(() => {
        const onVisibility = () => {
            const visible = document.visibilityState === 'visible';
            if (visible && currentChannelIdRef.current) setUnread(prev => ({ ...prev, [currentChannelIdRef.current]: { count: 0, hasMention: false } }));
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    // ── 내 접속 상태 ────────────────────────────────────────
    //  온라인: 이 창을 보고 있음 / 자리 비움: 창은 열려 있지만 다른 창·앱을 보는 중
    //  방해 금지: 알림(푸시·데스크톱 알림·소리)을 모두 꺼 둠 — 보고 있는지 여부도 함께 전달
    const notifyPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    const isDnd = !(pushOn || messageSoundEnabled || mentionSoundEnabled || (notifyPermission === 'granted' && (notifyEnabled || mentionNotifyEnabled)));
    const [myPresence, setMyPresence] = useState({ active: true, dnd: isDnd, device: detectDevice() });
    const lastPresenceKey = useRef('');
    const sendPresenceRef = useRef(() => {});
    sendPresenceRef.current = (force = false) => {
        const visible = document.visibilityState === 'visible';
        const next = { visible, active: visible && document.hasFocus(), dnd: isDnd, device: detectDevice() };
        const key = JSON.stringify(next);
        if (!force && key === lastPresenceKey.current) return;
        lastPresenceKey.current = key;
        setMyPresence(next);
        sendWsMessage({ type: 'presence', ...next });
    };
    useEffect(() => { sendPresenceRef.current(); }, [isDnd]);
    useEffect(() => {
        let timer = null;
        // 창 전환 직후 포커스 값이 흔들리므로 잠깐 기다렸다가 확인
        const update = () => { clearTimeout(timer); timer = setTimeout(() => sendPresenceRef.current(), 250); };
        const events = [['visibilitychange', document], ['focus', window], ['blur', window], ['pageshow', window]];
        events.forEach(([name, target]) => target.addEventListener(name, update));
        const poll = setInterval(() => sendPresenceRef.current(), 15000);
        return () => { clearTimeout(timer); clearInterval(poll); events.forEach(([name, target]) => target.removeEventListener(name, update)); };
    }, []);

    // 푸시 알림을 눌렀을 때 (이미 열린 앱 창) → 해당 메시지로 이동
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return undefined;
        const onMessage = (e) => {
            if (e.data?.type === 'OPEN_MESSAGE') navigateToMessage(e.data.channelId, e.data.messageId);
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, [navigateToMessage]);

    // ── 메시지 전송 / 조작 ──────────────────────────────────
    // 전송 중 표시를 붙여 보내고, 서버가 저장 결과를 돌려주지 않으면 실패로 표시 (다시 보내기 가능)
    const sendChat = useCallback((channelId, message, replyToId = null, clientId = newClientId()) => {
        updatePending(channelId, list => (list.some(p => p.clientId === clientId)
            ? list.map(p => (p.clientId === clientId ? { ...p, status: 'sending', error: null } : p))
            : [...list, { clientId, message, replyToId, status: 'sending' }]));
        clearTimeout(pendingTimers.current[clientId]);
        pendingTimers.current[clientId] = setTimeout(() => failPending(clientId, '응답이 없어 전송하지 못했을 수 있습니다.'), SEND_TIMEOUT_MS);
        sendWsMessage({ type: 'chat', channelId, message, replyToId, clientId });
    }, [sendWsMessage, updatePending, failPending]);

    const handleSendMessage = useCallback(async (message) => {
        if (!currentChannelId) return;
        const channelId = currentChannelId;
        const links = [...message.matchAll(CHAT_LINK_RE)];
        const replyToId = replyingTo?.id || null;
        setReplyingTo(null);
        if (!links.length) return sendChat(channelId, message, replyToId);

        // 채팅 메시지 링크는 미리보기 카드로 변환해서 보낸다
        const textOnly = message.replace(CHAT_LINK_RE, '').trim();
        if (textOnly) sendChat(channelId, textOnly, replyToId);
        for (const [raw, chId, msgId] of links) {
            try {
                const d = await apiJson(`/api/channels/${chId}/messages/${msgId}`);
                const preview = { channelId: d.channelId, channelName: d.channelName, msgId: d.id, username: d.username, message: d.message, timestamp: d.timestamp };
                sendChat(channelId, `${CHAT_LINK_PREFIX}${JSON.stringify(preview)}${CHAT_LINK_SUFFIX}`);
            } catch {
                sendChat(channelId, raw);
            }
        }
    }, [currentChannelId, replyingTo, sendChat]);

    const retryPending = useCallback((item) => {
        const chId = Number(Object.entries(pending).find(([, list]) => list.some(p => p.clientId === item.clientId))?.[0]);
        if (chId) sendChat(chId, item.message, item.replyToId, item.clientId);
    }, [pending, sendChat]);
    const discardPending = useCallback((item) => {
        sendWsMessage.cancel(item.clientId);
        resolvePending(item.clientId);
    }, [sendWsMessage, resolvePending]);

    const { uploadFiles, uploadProgress, cancelUpload, retryFailed, dismissProgress } = useFileUpload(handleSendMessage, currentChannelId);

    const handleFilesSelected = useCallback(async (files) => {
        if (!files?.length) return;
        if (files.some(f => isPreviewableMime(f.type))) return setPendingFiles(files);
        const ok = await confirmDialog(files.length > 1 ? `${files.length}개의 파일을 전송하시겠습니까?` : `${files[0].name} 파일을 전송하시겠습니까?`);
        if (ok) uploadFiles(files);
    }, [uploadFiles, confirmDialog]);

    const handleDeleteMessage = useCallback(async (messageId, skipConfirm) => {
        if (skipConfirm || await confirmDialog('메시지를 삭제하시겠습니까?')) sendWsMessage({ type: 'delete_message', messageId, channelId: currentChannelId });
    }, [sendWsMessage, currentChannelId, confirmDialog]);

    const handleRollbackMessage = useCallback(async (messageId, skipConfirm) => {
        if (!isAdmin || !(skipConfirm || await confirmDialog('메시지를 복구하시겠습니까?'))) return;
        try { await apiJson(`/api/messages/${messageId}/rollback`, { method: 'POST', json: { channelId: currentChannelId } }); }
        catch { toast.error('복구에 실패했습니다.'); }
    }, [isAdmin, currentChannelId, confirmDialog]);

    const handleEditMessage = useCallback((messageId, newMessage) => sendWsMessage({ type: 'edit_message', messageId, newMessage, channelId: currentChannelId }), [sendWsMessage, currentChannelId]);
    const sendReact = useCallback((messageId, emoji) => sendWsMessage({ type: 'react', messageId, emoji, channelId: currentChannelId }), [sendWsMessage, currentChannelId]);
    // 입력 중 표시는 2초에 한 번만 (한글은 자모마다 입력 이벤트가 나서 키 입력마다 보내면 전송 한도를 금방 채운다)
    const lastTypingRef = useRef(0);
    const sendTyping = useCallback(() => {
        const now = Date.now();
        if (now - lastTypingRef.current < 2000) return;
        lastTypingRef.current = now;
        sendWsMessage({ type: 'typing', channelId: currentChannelId });
    }, [sendWsMessage, currentChannelId]);
    const handleSetReply = useCallback((msg) => { setReplyingTo(msg); inputRef.current?.focus(); }, []);
    const handleEditingChange = useCallback((fn) => { activeEditRef.current = fn; }, []);
    const handleMediaClick = useCallback((items, index) => setLightbox({ items, index }), []);
    const handleRead = useCallback((messageId) => markRead(currentChannelIdRef.current, messageId), [markRead]);

    // 입력창에서 ↑: 내가 보낸 마지막 메시지 수정
    const handleEditLast = useCallback(() => {
        const items = messagesRef.current.getChannel(currentChannelIdRef.current).items;
        const mine = [...items].reverse().find(m => m.userId === clientData.userId && isEditableMessage(m));
        if (mine) setEditRequest({ msgId: mine.id, nonce: Date.now() });
    }, [clientData.userId]);

    // 메시지 저장 (회원 전용)
    const toggleSave = useCallback(async (msg) => {
        const chId = msg.channelId || currentChannelIdRef.current;
        const key = `${chId}:${msg.id}`;
        const saved = savedKeys.has(key);
        setSavedKeys(prev => { const next = new Set(prev); if (saved) next.delete(key); else next.add(key); return next; });
        try {
            if (saved) await apiJson(`/api/saved/${chId}/${msg.id}`, { method: 'DELETE' });
            else { await apiJson('/api/saved', { method: 'POST', json: { channelId: chId, messageId: msg.id } }); toast.success('메시지를 저장했습니다. 🔖 버튼에서 모아볼 수 있어요.'); }
        } catch (err) {
            setSavedKeys(prev => { const next = new Set(prev); if (saved) next.add(key); else next.delete(key); return next; });
            toast.error(err.message);
        }
    }, [savedKeys]);
    const removeSaved = useCallback(async (chId, msgId) => {
        await apiJson(`/api/saved/${chId}/${msgId}`, { method: 'DELETE' }).catch(() => {});
        setSavedKeys(prev => { const next = new Set(prev); next.delete(`${chId}:${msgId}`); return next; });
    }, []);

    // 채널 알림 수준 변경 (null = 기본값)
    const changeNotifyLevel = useCallback(async (level) => {
        const chId = currentChannelIdRef.current;
        const prevLevel = notifyLevelsRef.current[chId] ?? null;
        setNotifyLevels(prev => ({ ...prev, [chId]: level }));
        try {
            await apiJson(`/api/channels/${chId}/notify`, { method: 'POST', json: { level } });
            toast.success(level === 'none' ? '이 채널 알림을 껐습니다.' : level === 'all' ? '이 채널의 모든 메시지를 알려드려요.' : '멘션일 때만 알려드려요.');
        } catch (err) {
            setNotifyLevels(prev => ({ ...prev, [chId]: prevLevel }));
            toast.error(err.message);
        }
    }, []);

    const mentionUser = useCallback((userOrObj) => {
        const textarea = inputRef.current;
        if (!textarea) return;
        const mention = `@${typeof userOrObj === 'object' ? userOrObj.username : userOrObj} `;
        textarea.focus();
        const start = textarea.selectionStart;
        if (!document.execCommand('insertText', false, mention)) textarea.value = textarea.value.slice(0, start) + mention + textarea.value.slice(start);
        textarea.setSelectionRange(start + mention.length, start + mention.length);
    }, []);

    const searchMessages = useCallback((q, channelId, username, filters = {}) => {
        const params = new URLSearchParams({ q, channelId, limit: '50' });
        if (username) params.set('username', username);
        Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
        return apiJson(`/api/messages/search?${params}`).catch(() => []);
    }, []);
    const searchMessageUsers = useCallback((channelId) => apiJson(`/api/messages/search-users?channelId=${encodeURIComponent(channelId)}`).catch(() => []), []);

    const openNotifications = async () => {
        setNotifications([]);
        try { setNotifications(await apiJson('/api/notifications')); } catch {}
        apiFetch('/api/notifications/read-all', { method: 'POST' }).then(() => setUnreadNotifCount(0)).catch(() => {});
    };

    const savePin = async (text) => {
        try { await apiJson(`/api/channels/${currentChannelId}/pin`, { method: 'POST', json: { message: text } }); return true; }
        catch (err) { toast.error(err.message); return false; }
    };
    const removePin = async () => {
        if (!(await confirmDialog('공지를 삭제하시겠습니까?'))) return;
        apiJson(`/api/channels/${currentChannelId}/pin`, { method: 'DELETE' }).catch(err => toast.error(err.message));
    };

    // ── 레이아웃 상호작용 ───────────────────────────────────
    // 답장 배너가 나타나고 사라질 때 채팅 스크롤 위치가 밀리지 않도록 보정
    const replyScrollRef = useRef({ prev: null, fromBottom: 0 });
    useLayoutEffect(() => {
        const el = document.getElementById('chat');
        const s = replyScrollRef.current;
        if (el && s.prev === null && replyingTo) s.fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (el && s.prev && !replyingTo) el.scrollTop = el.scrollHeight - el.clientHeight - s.fromBottom;
        s.prev = replyingTo;
    }, [replyingTo]);

    const startPanelResize = useCallback((panel, startEvent) => {
        if (isCompact || (panel === 'members' && isNarrow)) return;
        startEvent.preventDefault();
        const startX = startEvent.clientX;
        const startWidth = panel === 'channel' ? channelSidebarWidth : memberPanelWidth;
        setLayoutResizing(true);
        document.body.classList.add('is-panel-resizing');
        const onMove = (e) => {
            const delta = (e.clientX - startX) / uiZoom();
            if (panel === 'channel') setChannelSidebarWidth(Math.max(180, Math.min(360, startWidth + delta)));
            else setMemberPanelWidth(Math.max(220, Math.min(360, startWidth - delta)));
        };
        const onUp = () => {
            setLayoutResizing(false);
            document.body.classList.remove('is-panel-resizing');
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [channelSidebarWidth, memberPanelWidth, isCompact, isNarrow]);

    const currentChannel = channels.find(c => c.id === currentChannelId);
    const canUpload = currentChannelId !== null && !(currentChannel?.is_announcement && !isAdmin);

    // 파일 드래그 앤 드롭 (파일을 끌어올 때만 반응 — 페이지 안의 글자·이미지를 끄는 경우는 무시)
    useEffect(() => {
        const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
        let depth = 0;
        const enter = (e) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            depth++;
            setIsDragging(true);
        };
        const over = (e) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = canUpload ? 'copy' : 'none';
        };
        const leave = (e) => {
            if (!hasFiles(e)) return;
            depth = Math.max(0, depth - 1);
            if (!depth) setIsDragging(false);
        };
        const drop = (e) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            depth = 0;
            setIsDragging(false);
            if (!canUpload) return toast.error(currentChannelId === null ? '채널을 먼저 선택하세요.' : '공지 전용 채널에는 파일을 올릴 수 없습니다.');
            if (e.dataTransfer.files?.length) handleFilesSelected(Array.from(e.dataTransfer.files));
        };
        window.addEventListener('dragenter', enter); window.addEventListener('dragover', over);
        window.addEventListener('dragleave', leave); window.addEventListener('drop', drop);
        return () => {
            window.removeEventListener('dragenter', enter); window.removeEventListener('dragover', over);
            window.removeEventListener('dragleave', leave); window.removeEventListener('drop', drop);
        };
    }, [handleFilesSelected, canUpload, currentChannelId]);

    // 서랍은 항상 하나만: 나중에 연 쪽을 남기고 다른 쪽은 닫는다 (버튼·스와이프 어느 경로로 열어도)
    const drawerRef = useRef({ channel: false, members: false });
    drawerRef.current = { channel: isChannelSidebarOpen, members: !isUserListCollapsed };
    useEffect(() => { if (isChannelSidebarOpen && isNarrow) setUserListCollapsed(true); }, [isChannelSidebarOpen]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (!isUserListCollapsed && isCompact) setChannelSidebarOpen(false); }, [isUserListCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

    // 터치 스와이프: 왼쪽 가장자리 → 채널 목록(서랍 모드) / 좌우 → 접속자 패널(겹침 모드)
    useEffect(() => {
        if (!isTouchDevice && !isMobile) return undefined;
        if (!isNarrow) return undefined;
        let sx = 0, sy = 0, ignored = false;
        const hScrollable = (el) => {
            for (; el && el !== document.body; el = el.parentElement) {
                const ox = window.getComputedStyle(el).overflowX;
                if (el.scrollWidth > el.clientWidth + 4 && (ox === 'auto' || ox === 'scroll')) return true;
            }
            return false;
        };
        const start = (e) => { gestureState.replySwipe = false; sx = e.touches[0].clientX; sy = e.touches[0].clientY; ignored = hScrollable(e.target) || !!e.target.closest('.edit-area, .lightbox-overlay, .sheet-backdrop, .search-panel'); };
        const end = (e) => {
            // 메시지를 밀어 답장한 손짓은 서랍 열기로 치지 않는다 (답장도 오른쪽→왼쪽이라 겹친다)
            if (gestureState.replySwipe) { gestureState.replySwipe = false; return; }
            if (ignored) return;
            const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
            if (Math.abs(dy) > Math.abs(dx) * 1.2 || Math.abs(dx) < 60) return;
            // 열려 있는 서랍이 있으면 그 서랍을 닫는 동작만 한다 (닫으려고 밀었는데 반대쪽 서랍이 열리지 않게)
            if (drawerRef.current.channel) { if (dx < 0) setChannelSidebarOpen(false); return; }
            if (drawerRef.current.members) { if (dx > 0) setUserListCollapsed(true); return; }
            // 왼쪽 가장자리에서 오른쪽으로 밀면 채팅 목록으로 (iOS 뒤로 가기 손짓)
            if (dx > 0 && sx < 30 && isCompact) { goListRef.current?.(); return; }
            if (dx < 0) setUserListCollapsed(false);
        };
        document.addEventListener('touchstart', start, { passive: true });
        document.addEventListener('touchend', end, { passive: true });
        return () => { document.removeEventListener('touchstart', start); document.removeEventListener('touchend', end); };
    }, [isCompact, isNarrow]);

    // Alt+↑/↓, Alt+숫자 채널 이동
    useEffect(() => {
        const onKey = (e) => {
            if (!e.altKey || ['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) return;
            const sorted = sortChannelsLikeSidebar(channels, categoryOrder);
            if (!sorted.length) return;
            const idx = sorted.findIndex(c => c.id === currentChannelId);
            let next = null;
            if (e.key === 'ArrowDown' || e.key === 'j') next = sorted[(idx + 1) % sorted.length];
            else if (e.key === 'ArrowUp' || e.key === 'k') next = sorted[(idx - 1 + sorted.length) % sorted.length];
            else if (e.key >= '1' && e.key <= '9') next = sorted[Number(e.key) - 1];
            if (next) { e.preventDefault(); selectChannel(next.id); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [channels, categoryOrder, currentChannelId, selectChannel]);

    // 휴대폰 채팅 방 → 목록. 지금 방을 닫아 둬야 목록을 보는 동안 그 방 새 메시지가 안 읽음으로 남는다.
    const goToPhoneList = useCallback(() => { setPhoneList(true); setCurrentChannelId(null); setReplyingTo(null); }, []);
    const goListRef = useRef(null);
    goListRef.current = phoneList ? null : goToPhoneList;
    // 휴대폰에서 방에 들어가 있으면 뒤로가기 = 목록으로
    useBackClose(isCompact && !phoneList, goToPhoneList);
    // 뒤로 버튼에 다른 방의 안 읽은 수 (알림 끈 채널은 멘션만)
    const otherUnread = Object.entries(unread).reduce((sum, [chId, u]) => (
        Number(chId) === currentChannelId || (notifyLevels[chId] === 'none' && !u?.hasMention) ? sum : sum + (u?.count || 0)
    ), 0);

    // ── 렌더링 ──────────────────────────────────────────────
    const channelState = messages.getChannel(currentChannelId);
    const handleLoadOlder = useCallback(() => loadOlder(currentChannelId), [loadOlder, currentChannelId]);
    const handleLoadNewer = useCallback(() => loadNewer(currentChannelId), [loadNewer, currentChannelId]);
    const handleJumpToLatest = useCallback(() => { setTarget(null); loadLatest(currentChannelId); }, [loadLatest, currentChannelId]);
    const targetMsgId = target && target.ready && target.chId === currentChannelId ? target.msgId : null;
    const awaitingTarget = !!target && target.chId === currentChannelId;

    const channelTitle = currentChannel
        ? (isDmChannel(currentChannel) ? currentChannel.description : currentChannel.name)
        : (channels.length ? 'Break Chat' : '채널 없음');
    const typingList = Object.keys(typingUsers[currentChannelId] || {});

    return (
        <div className={`site-page chat-page${isCompact && !phoneList ? ' phone-room' : ''}`}>
            {confirmModal}
            {pendingFiles && (
                <ImagePreviewModal files={pendingFiles}
                    onConfirm={(ordered, options) => { setPendingFiles(null); uploadFiles(ordered, options); }}
                    onCancel={() => setPendingFiles(null)} />
            )}
            {lightbox && <Lightbox mediaItems={lightbox.items} initialIndex={lightbox.index} onClose={() => setLightbox(null)} />}
            {guestNickModal && (
                <GuestNicknameModal currentName={clientData.username} error={guestNickModal.error}
                    onSubmit={(name) => { setGuestNickModal({ error: '' }); sendWsMessage({ type: 'change_nickname', newUsername: name }); }}
                    onClose={() => setGuestNickModal(null)} />
            )}
            {showAccountSettings && (
                <AccountSettingsModal clientData={clientData} onClose={() => setShowAccountSettings(false)} onLogout={handleLogout}
                    onPrefsChange={(prefs) => { autoCollapsePin.current = prefs?.autocollapsePin !== false; if (currentChannelIdRef.current) setPinCollapsed(pinCollapsedFor(currentChannelIdRef.current)); }} />
            )}

            <SiteNav
                actions={
                    <ChatNavActions
                        isMember={isMember} unreadNotifCount={unreadNotifCount} onOpenNotifications={openNotifications}
                        membersOpen={!isUserListCollapsed} onToggleMembers={toggleMemberPanel}
                    />
                }
                account={{
                    label: isMember ? '계정 설정 · 프로필 사진' : '닉네임 변경',
                    onOpen: () => (isMember ? setShowAccountSettings(true) : setGuestNickModal({ error: '' })),
                }}
                settings={
                    <NotificationSettings
                        notifyEnabled={notifyEnabled} setNotify={setNotifyEnabled}
                        mentionNotifyEnabled={mentionNotifyEnabled} setMentionNotify={setMentionNotifyEnabled}
                        messageSoundEnabled={messageSoundEnabled} setMessageSound={setMessageSoundEnabled}
                        mentionSoundEnabled={mentionSoundEnabled} setMentionSound={setMentionSoundEnabled}
                        isMember={isMember} pushOn={pushOn} setPushOn={setPushOn} myPresence={myPresence}
                    />
                }
            />
            {notifications && (
                <NotificationPanel notifications={notifications}
                    onClose={() => setNotifications(null)}
                    onMarkAllRead={() => { setNotifications(n => n.map(x => ({ ...x, is_read: 1 }))); setUnreadNotifCount(0); }}
                    onNavigate={(chId, msgId) => { setNotifications(null); navigateToMessage(chId, msgId); }} />
            )}

            <div className={`chat-layout${isLayoutResizing ? ' resizing' : ''}`}>
            {(!isCompact || showPhoneList) && (
                <ChannelSidebar channels={channels} categoryOrder={categoryOrder} currentChannelId={currentChannelId} onSelect={selectChannel}
                    isAdmin={isAdmin} isOpen={isChannelSidebarOpen && !isCompact} onClose={() => setChannelSidebarOpen(false)} unread={unread} width={channelSidebarWidth}
                    notifyLevels={notifyLevels} asScreen={showPhoneList} />
            )}
            {!isCompact && <div className="panel-resizer" onMouseDown={(e) => startPanelResize('channel', e)} title="채널 영역 너비 조절" />}

            {!showPhoneList && (
            <div className="main-content">
                <div className={`drag-overlay ${isDragging ? 'active' : ''}${canUpload ? '' : ' blocked'}`}
                    data-label={canUpload ? `#${channelTitle} 채널에 파일 올리기` : '이 채널에는 파일을 올릴 수 없습니다'} />
                <header className="chat-header">
                    <div className="chat-header-row">
                        {isCompact && (
                            <button className="chat-back-btn" onClick={goToPhoneList} title="채팅 목록" aria-label={otherUnread ? `채팅 목록, 안 읽은 메시지 ${otherUnread}개` : '채팅 목록'}>
                                <Icon name="chevronLeft" size={34} />
                                {otherUnread > 0 && <span className="chat-back-badge">{otherUnread > 99 ? '99+' : otherUnread}</span>}
                            </button>
                        )}
                        <div className="chat-title">
                            <h1>{currentChannel && <ChannelIcon channel={currentChannel} className="chat-title-icon" />}<span className="chat-title-text">{channelTitle}</span></h1>
                            {currentChannel?.description && !isDmChannel(currentChannel) && <span className="chat-title-desc">{currentChannel.description}</span>}
                        </div>
                        <div className="chat-header-actions" ref={setHeaderActions} />
                        {isCompact && (
                            <button className={`chat-tool-btn${!isUserListCollapsed ? ' active' : ''}`} onClick={toggleMemberPanel} title="접속자 목록" aria-label="접속자 목록"><Icon name="users" /></button>
                        )}
                    </div>
                    {currentChannelId && (
                        <ChannelPinBar key={currentChannelId} pin={channelPins[currentChannelId]} isAdmin={isAdmin}
                            isNew={newPinChannels.has(currentChannelId)} collapsed={pinCollapsed}
                            pinMode={pinModes[currentChannelId] || ''} onChangePinMode={(mode) => changePinMode(currentChannelId, mode)}
                            onToggleCollapsed={() => { setPinCollapsed(v => !v); markPinSeen(currentChannelId); }}
                            onSave={savePin} onRemove={removePin} />
                    )}
                </header>

                {currentChannelId === null ? (
                    <div className="chat-empty">
                        <p>채널이 없습니다.</p>
                        {isAdmin && <p className="chat-empty-sub">관리자 페이지 → 채널에서 채널을 만들어 주세요.</p>}
                    </div>
                ) : messages.loadError.initial && !channelState.items.length ? (
                    <div className="chat-empty">
                        <p>메시지를 불러오지 못했습니다.</p>
                        <p className="chat-empty-sub">연결을 확인한 뒤 다시 시도해 주세요.</p>
                        <button className="load-retry-btn" onClick={handleJumpToLatest}>다시 시도</button>
                    </div>
                ) : messages.loading.initial && !channelState.items.length ? (
                    <div className="chat-empty"><span className="load-spinner" aria-hidden="true" /> 메시지를 불러오는 중</div>
                ) : (
                    <ChatWindow
                        messages={channelState.items} generation={channelState.generation} isAdmin={isAdmin} clientData={clientData}
                        channelId={currentChannelId} channels={channels}
                        hasOlder={channelState.hasOlder} hasNewer={channelState.hasNewer}
                        loadingOlder={messages.loading.older} loadingNewer={messages.loading.newer} loadError={messages.loadError}
                        onLoadOlder={handleLoadOlder} onLoadNewer={handleLoadNewer} onJumpToLatest={handleJumpToLatest}
                        targetMsgId={targetMsgId} awaitingTarget={awaitingTarget} onTargetShown={clearTarget}
                        onSearch={searchMessages} onSearchUsers={searchMessageUsers} onChannelSelect={selectChannel} onNavigateToMsg={navigateToMessage}
                        setReply={handleSetReply} onDelete={handleDeleteMessage} onRollback={handleRollbackMessage} onMediaClick={handleMediaClick}
                        onEdit={handleEditMessage} onMentionUser={mentionUser} sendReact={sendReact} onEditingChange={handleEditingChange}
                        lastReadId={lastReadOf(currentChannelId)} onRead={handleRead}
                        pending={pending[currentChannelId] || []} onRetryPending={retryPending} onDiscardPending={discardPending}
                        savedKeys={savedKeys} onToggleSave={isMember ? toggleSave : undefined} onRemoveSaved={removeSaved}
                        editRequest={editRequest}
                        isAnnouncement={!!currentChannel?.is_announcement}
                        notifyLevel={notifyLevels[currentChannelId] || null} defaultNotifyLevel={defaultNotifyOf(currentChannel)}
                        onChangeNotify={isMember ? changeNotifyLevel : undefined}
                        toolbarSlot={headerActions}
                    />
                )}

                <div className="chat-input-dock">
                    <TypingIndicator users={typingList} />
                    <UploadProgress progress={uploadProgress} onCancel={cancelUpload} onRetry={retryFailed} onDismiss={dismissProgress} />
                    {replyingTo && <ReplyingBanner replyingTo={replyingTo} onCancel={() => setReplyingTo(null)} />}
                    {currentChannelId !== null && (
                        <InputForm ref={inputRef} onSendMessage={handleSendMessage} onFilesSelected={handleFilesSelected}
                            onlineUsers={onlineUsers} myUsername={clientData.username} channels={channels} onSelectChannel={selectChannel}
                            onTyping={sendTyping}
                            isAnnouncementChannel={!!currentChannel?.is_announcement && !isAdmin} activeEditRef={activeEditRef}
                            channelId={currentChannelId} onEditLast={handleEditLast} />
                    )}
                </div>
            </div>
            )}
            {!isUserListCollapsed && !isNarrow && <div className="panel-resizer" onMouseDown={(e) => startPanelResize('members', e)} title="멤버 영역 너비 조절" />}
            <UserList users={onlineUsers} systemMessages={systemMessages} open={!isUserListCollapsed} onClose={() => setUserListCollapsed(true)}
                onMentionUser={mentionUser} width={memberPanelWidth} channelName={channelTitle} />
            </div>
        </div>
    );
}
